import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoryService, productService } from '../../services/api';
import { useFilters } from '../../contexts/FilterContext';
import { useTopRatedProducts } from '../../hooks/useProducts';
import { getProductImageUrl } from '../../utils/imageHelper';
import styles from './Sidebar.module.css';

const Sidebar = () => {
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [maxPrice, setMaxPrice] = useState(100000);
  const [currentMaxPrice, setCurrentMaxPrice] = useState(100000);
  const { updateCategoryFilter, updateMaxPrice, categoryFilter } = useFilters();
  const { products: topRatedProducts, isLoading: isLoadingTopRated } = useTopRatedProducts();
  const navigate = useNavigate();

  useEffect(() => {
    loadCategories();
    loadMaxPrice();
  }, []);

  const loadCategories = async () => {
    try {
      setIsLoadingCategories(true);
      const response = await categoryService.getCategories();
      if (response.success) {
        const categoriesData = Array.isArray(response.data)
          ? response.data
          : (response.data?.data || []);
        setCategories(categoriesData);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const loadMaxPrice = async () => {
    try {
      const response = await productService.getMaxPrice();
      if (response.success && response.data?.max_price) {
        const price = Math.ceil(response.data.max_price);
        setMaxPrice(price);
        setCurrentMaxPrice(price);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du prix maximum:', error);
    }
  };

  const handlePriceChange = (e) => {
    const newPrice = parseInt(e.target.value);
    setCurrentMaxPrice(newPrice);
    updateMaxPrice(newPrice);
  };

  const handleCategoryClick = (categoryId) => {
    if (categoryFilter === categoryId) {
      updateCategoryFilter(''); // Désélectionner si déjà sélectionné
    } else {
      updateCategoryFilter(categoryId);
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  // Gérer l'image du produit
  const getProductImage = (product) => {
    if (product.primary_image) {
      return getProductImageUrl(product.primary_image);
    }
    if (product.images?.[0]) {
      return getProductImageUrl(product.images[0]);
    }
    return '/images/leaves-right.png';
  };

  // Gérer le prix
  const getPrice = (product) => {
    const currentPrice = parseFloat(product.sale_price || product.price || 0);
    return currentPrice;
  };

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarSection}>
        <h3>Filtrer par prix</h3>
        <div className={styles.filterPrice}>
          <input
            type="range"
            min="0"
            max={maxPrice}
            value={currentMaxPrice}
            onChange={handlePriceChange}
            className={styles.priceRange}
          />
          <div className={styles.priceValues}>
            <span>0 FCFA</span>
            <span>{currentMaxPrice.toLocaleString('fr-FR')} FCFA</span>
          </div>
        </div>
      </div>

      <div className={styles.sidebarSection}>
        <h3>Catégories de produits</h3>
        {isLoadingCategories ? (
          <div className={styles.loading}>Chargement...</div>
        ) : (
          <ul className={styles.categoriesList}>
            {categories.length > 0 ? (
              categories.map(category => (
                <li key={category.id}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleCategoryClick(category.id);
                    }}
                    className={categoryFilter === category.id ? styles.active : ''}
                  >
                    {category.name}
                    <span>({category.products_count || 0})</span>
                  </a>
                </li>
              ))
            ) : (
              <li className={styles.noCategories}>Aucune catégorie disponible</li>
            )}
          </ul>
        )}
      </div>

      <div className={styles.sidebarSection}>
        <h3>Produits les mieux notés</h3>
        {isLoadingTopRated ? (
          <div className={styles.loading}>Chargement...</div>
        ) : (
          <div className={styles.topRatedProductsSidebar}>
            {topRatedProducts.length > 0 ? (
              topRatedProducts.slice(0, 3).map(product => {
                const imageUrl = getProductImage(product);
                const price = getPrice(product);

                return (
                  <div
                    key={product.id}
                    className={styles.topRatedProduct}
                    onClick={() => handleProductClick(product.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={styles.topRatedImage}>
                      <img src={imageUrl} alt={product.name} />
                    </div>
                    <div className={styles.topRatedInfo}>
                      <div className={styles.topRatedTitle}>{product.name}</div>
                      <div className={styles.topRatedPrice}>
                        {price.toLocaleString('fr-FR')} FCFA
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={styles.noProducts}>Aucun produit noté</div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;